import { ReactElement } from 'react';
import { ADD_TO_CART_DISPLAY_TYPE, AddToCart } from '@client/cart/components/AddToCart';
import { useTranslate } from '@client/i18n/hooks';
import { ProductActions } from '@client/product/components/ProductActions';
import { BulkPrices } from '@client/product/components/ProductData/BulkPrices/BulkPrices';
import { Features } from '@client/product/components/ProductData/Features';
import { PriceList } from '@client/product/components/ProductData/PriceList';
import { Props } from '@client/product/components/ProductData/ProductData';
import { ProductVariants } from '@client/product/components/ProductData/ProductVariants';
import { useGetEnergyLabelImageBySku } from '@client/product/hooks/useGetEnergyLabelImageBySku';
import { getCdnImageUrl } from '@client/routes/utils/url';
import { PRODUCT_LABEL_SIZE } from 'b2b-components/core/components/ProductLabel/ProductLabel/ProductLabel';
import { AttributesList } from 'b2b-components/theme//components/AttributesList';
import { EnergyLabel } from 'b2b-components/theme/components/EnergyLabel';
import { Icon, ICON_NAME, ICON_SIZE } from 'b2b-components/theme/components/Icon';
import { LabelList } from 'b2b-components/theme/components/ProductList/ProductBox/common/LabelList';
import { USBCharger } from 'b2b-apps/core/product/components/ProductData/USBCharger';
import { getUSBCDataFromSpecifications } from 'b2b-apps/core/product/utils/getUSBCDataFromSpecifications';
import './ProductData.scss';

export const ProductData = ({ product, onAddToCart }: Props): ReactElement => {
  const t = useTranslate();
  const {
    effectivePricing,
    description: { energyLabel, specification },
    settings,
    sku,
    variants,
  } = product;

  const energyLabelImage = useGetEnergyLabelImageBySku(sku);
  const energyLabelImageUrl = getCdnImageUrl(energyLabelImage?.sizes?.full);
  const energyLabelText = {
    close: t('close'),
    energyLabel: t('Energy Label'),
  };
  const usbCData = getUSBCDataFromSpecifications(specification);
  const boughtLabel = product?.bought
    ? t(
      [
        'You have previously bought this item on {date} (×1 pc.)',
        'You have previously bought this item on {date} (×{quantity} pcs.)',
        'quantity',
      ],
      {
        quantity: product.bought.quantity,
        date: product.bought.date,
      },
    )
    : null;

  const {
    isNew,
    isOccasion,
    isSellout,
    isSpecialOffer,
    isCoreAssortment,
    isOwnAssortment,
    isConsciousAlternative,
  } = settings;

  const translation = product.description;

  return (
    <div className='ProductData'>
      <LabelList
        size={PRODUCT_LABEL_SIZE.small}
        className='ProductData-Labels'
        isNew={isNew}
        isOccasion={isOccasion}
        isSellout={isSellout}
        isPromotion={isSpecialOffer}
        isCoreAssortment={isCoreAssortment}
        isOwnAssortment={isOwnAssortment}
        isConsciousAlternative={isConsciousAlternative}
        text={{
          new: t('new flag', {}, 'Product flag icon - "new" label'),
          occasion: t('verified return flag', {}, 'Product flag icon - "verified return" label'),
          promotion: t('special offer flag', {}, 'Product flag icon - "special offer" label'),
          sellout: t('sale flag', {}, 'Product flag icon - "sale" label'),
          coreAssortment: t('Core assortment'),
          ownAssortment: t('Customer specific assortment'),
          consciousAlternative: t('Conscious alternative'),
        }}
      />
      <h1 className='ProductData-Title'>{translation.title}</h1>

      {energyLabel && (
        <EnergyLabel
          className='ProductData-EnergyLabel'
          range={energyLabel.range}
          rating={energyLabel.value}
          energyLabelUrl={energyLabelImageUrl}
          text={energyLabelText}
        />
      )}

      <div className='ProductData-ActionsMobile'>
        <PriceList
          effectivePricing={effectivePricing}
          className='ProductData-Pricelist'
        />
        <ProductActions
          className='order-0 order-sm-1'
          product={product}
        />
      </div>

      <div className='ProductData-ActionsWrapper'>
        <div
          className='ProductData-Buy order-1 order-sm-0'
          data-testid='addToCartPDP'
        >
          <AddToCart
            type={ADD_TO_CART_DISPLAY_TYPE.full}
            sku={product.sku}
            productName={translation.title}
            settings={product.settings}
            isUserCertified={effectivePricing.isCertified}
            onClick={onAddToCart}
            dataAttributes={{ 'data-testid': 'pdpAddToCartButton' }}
          />
        </div>
        <ProductActions
          className='ProductData-HideActions order-0 order-sm-1'
          product={product}
        />
      </div>

      <BulkPrices
        className='ProductData-BulkPrices--Mobile'
        discounts={product.scaledDiscounts}
      />

      <AttributesList attributes={translation.bulletPoints} withPadding className='ProductData-Attributes' />

      <Features
        sku={product.sku}
        ean={product.ean}
        manufacturerProductId={product.manufacturer.productId}
        manufacturerUrl={product.manufacturer.url}
        manufacturerName={product.manufacturer.name}
        manufacturerWarranty={product.manufacturer.warranty}
        manufacturerInternalPage={product.manufacturer.internalPage}
        soldSince={product.webDateStart}
        isSpecialOrder={product.settings.isSpecialOrder}
        quantityOrdered={product.settings.quantityOrdered}
        stockCount={product.settings.quantity}
        directDelivery={product.settings.directDelivery}
        isOccasion={isOccasion}
        isSellout={isSellout}
      />

      {usbCData.hasUsbCPort && <USBCharger {...usbCData} />}

      {!!variants?.length
        && <div className='ProductVariants-Wrapper'>
          <ProductVariants sku={sku} variantGroups={variants} />
        </div>
      }

      {product.bought && (
        <div className='ProductData-Bought'>
          <Icon className='ProductData-Bought-Icon' name={ICON_NAME.boxCheck} size={ICON_SIZE.small} />
          <span className='ProductData-Bought-Label'>{boughtLabel}</span>
        </div>
      )}

      <BulkPrices
        className="ProductData-BulkPrices--Desktop"
        discounts={product.scaledDiscounts}
      />
    </div>
  );
};
